import { ApiError } from '../../lib/api';

// ARCA 三联发票 PDF 的公共小工具。
//
// 为什么单列一个文件：「开票列表」「ARCA 财务专页」「合同详情 → 关联单据里的发票号」
// 三处都要下载同一张票的 PDF。若各自拼路径 / 各自翻译错误码，迟早出现三套文案
// （同一个「未归档」在一处说「文件不存在」、另一处说「服务异常」）。
// 这里只放**无 UI 状态**的纯函数，各页自己决定提示放在哪（行内 / 页面顶部 / notice）。

/** 三联发票 PDF 的下载路径（后端不受 INTEGRAL_ENABLE_FINANCE_READ 门控）。 */
export function invoicePdfPath(invoiceId: string): string {
  return `/api/arca-invoices/${encodeURIComponent(invoiceId)}/pdf`;
}

/**
 * 本地保存用的文件名。
 *
 * 不用后端 Content-Disposition 里的 filename：那个头可能是 latin-1 编码的 ARCA 票号，
 * 直接当 download 名在中文 Windows 上会乱码。
 *
 * 优先用**税务局票号**（销售点-票号）命名：现场口径是内部发票号不再采用，
 * 客户和财务都只认税务局的号，下载下来的文件名要能直接对上票面。
 * 没有票号时才退回内部编号（历史/异常数据），再没有就 ARCA。
 */
export function invoicePdfFilename(inv: {
  internal_number?: string | null;
  number?: string | null;
  point_of_sale?: string | number | null;
  voucher_number?: string | number | null;
}): string {
  const pos = String(inv.point_of_sale ?? '').trim();
  const nro = String(inv.voucher_number ?? '').trim();
  let base = '';
  if (pos || nro) {
    const posOut = /^\d+$/.test(pos) ? pos.padStart(5, '0') : pos;
    const nroOut = /^\d+$/.test(nro) ? nro.padStart(8, '0') : nro;
    base = `${posOut}-${nroOut}`;
  }
  if (!base) base = String(inv.internal_number || inv.number || 'ARCA').trim() || 'ARCA';
  // 文件名里不能出现路径分隔符等保留字符（票号本身只有数字和短横，这里是防御）。
  return `ARCA_${base.replace(/[\\/:*?"<>|]/g, '_')}.pdf`;
}

/**
 * 把 PDF 下载失败翻译成给人看的中文。
 *
 * 按 `code` 分支的原因：这几种都是**流程状态**而非故障，混成一句「下载失败」
 * 会让财务去反复重试或报修。例如 FISCAL_PDF_NOT_ARCHIVED 意味着这张票在税务
 * 工作台还没归档 PDF（要先去财务专页看原始报文/附件），重试一百次也没用。
 */
export function describeInvoicePdfError(e: unknown): string {
  if (e instanceof ApiError) {
    switch (e.code) {
      case 'FISCAL_PDF_NOT_ARCHIVED':
        return '这张票在税务账本里还没有归档 PDF（不是网络故障，重试无用）。原始报文与附件可在「ARCA 财务专页」查看。';
      case 'FISCAL_PDF_UNAVAILABLE':
        // 后端两处会发这个码：非已授权状态、或 factura 账本里按 CAE 找不到票。
        // 合并成一句是因为对用户来说处置相同 —— 这张票此刻没有可下发的税务 PDF。
        return '这张票当前没有可下发的税务 PDF（未授权，或 Factura 账本里找不到对应 CAE）。';
      case 'FISCAL_PDF_INTEGRITY':
        return '税票 PDF 与账本登记的校验和不一致，已拒绝下发。这是账本完整性告警，请联系管理员核查，不要用其它途径打印此票。';
      case 'FACTURA_UNAVAILABLE':
        return '无法连接 Factura 财务工作台（取票服务不可用），暂时取不到三联发票 PDF。';
      case 'FACTURA_UNAUTHORIZED':
        return 'Factura 拒绝了取票令牌（两侧配置不一致），需要管理员处理，重试无用。';
      case 'FACTURA_UPSTREAM_INVALID':
        return 'Factura 财务工作台返回了无法识别的内容（不是有效 PDF），已拒绝保存。';
      case 'FACTURA_CONFIG_INVALID':
        return 'Factura 取票地址配置不合法（服务端配置问题），请联系管理员。';
      default:
        return e.message || '发票 PDF 下载失败';
    }
  }
  return e instanceof Error ? e.message : '发票 PDF 下载失败';
}
